/**
 * ProfessionalCreateModal — admin create form for a new marketplace
 * professional.
 *
 * Fires POST /api/admin/marketplace/professionals with the CP1 dispatch
 * field set. On success, closes the modal and invokes `onCreated` with
 * the new pro so the parent list can prepend or refresh.
 */
import { useEffect, useMemo, useState } from "react";
import AdminModal from "@/components/v2/AdminModal";
import { api, formatApiError } from "@/api/client";

const CONTACT_PREFS = [
  { value: "platform_only", label: "Platform only (leads via consultation requests)" },
  { value: "email_intro", label: "Email intro (admin can share email)" },
  { value: "phone_visible", label: "Phone visible (public listing)" },
];

const RATE_RANGES = ["$", "$$", "$$$", "$$$$"];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID",
  "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS",
  "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK",
  "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV",
  "WI", "WY",
];

export default function ProfessionalCreateModal({ open, onClose, categories, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    categories: [],
    bio: "",
    service_area_mode: "nationwide",
    service_area_states: [],
    years_experience: 5,
    website_url: "",
    contact_email: "",
    public_contact_preference: "platform_only",
    phone_number: "",
    rate_range: "$$",
    verification_proof_url: "",
    is_demo: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!open) {
      setForm({
        name: "",
        categories: [],
        bio: "",
        service_area_mode: "nationwide",
        service_area_states: [],
        years_experience: 5,
        website_url: "",
        contact_email: "",
        public_contact_preference: "platform_only",
        phone_number: "",
        rate_range: "$$",
        verification_proof_url: "",
        is_demo: false,
      });
      setSubmitting(false);
      setError(null);
      setErrorCode(null);
      setFieldErrors({});
    }
  }, [open]);

  const activeCats = useMemo(
    () => (categories || []).filter((c) => c.is_active),
    [categories]
  );

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!form.name.trim()) return false;
    if (!form.categories.length) return false;
    if (!form.bio.trim()) return false;
    if (!form.contact_email.trim()) return false;
    if (form.service_area_mode === "states" && !form.service_area_states.length) return false;
    return true;
  }, [form, submitting]);

  function toggleCategory(slug) {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(slug)
        ? f.categories.filter((s) => s !== slug)
        : [...f.categories, slug],
    }));
  }

  function toggleState(code) {
    setForm((f) => ({
      ...f,
      service_area_states: f.service_area_states.includes(code)
        ? f.service_area_states.filter((s) => s !== code)
        : [...f.service_area_states, code],
    }));
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    setErrorCode(null);
    setFieldErrors({});
    const service_area =
      form.service_area_mode === "nationwide"
        ? "nationwide"
        : [...form.service_area_states].sort();
    const payload = {
      name: form.name.trim(),
      categories: form.categories,
      bio: form.bio.trim(),
      service_area,
      years_experience: Number(form.years_experience) || 0,
      website_url: form.website_url.trim() || null,
      contact_email: form.contact_email.trim(),
      public_contact_preference: form.public_contact_preference,
      phone_number: form.phone_number.trim() || null,
      rate_range: form.rate_range,
      verification_proof_url: form.verification_proof_url.trim() || null,
      is_demo: form.is_demo,
    };
    try {
      const { data } = await api.post("/admin/marketplace/professionals", payload);
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (status === 422 && code === "professional_content_failed_guardrail") {
        setError("The name contains language we can't process. Please rephrase.");
        setErrorCode(code);
        setFieldErrors({ name: true });
      } else if (status === 422 && code === "bio_failed_guardrail") {
        setError("The bio contains language we can't process. Please rephrase.");
        setErrorCode(code);
        setFieldErrors({ bio: true });
      } else if (status === 422 && code === "unknown_category") {
        setError(
          `One or more categories aren't active: ${(detail.unknown_slugs || []).join(", ")}`
        );
        setErrorCode(code);
        setFieldErrors({ categories: true });
      } else {
        setError(formatApiError(err));
        setErrorCode("generic");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    "w-full rounded-md border bg-transparent px-3 py-2 text-sm";
  const inputStyle = { borderColor: "var(--v2-border)", color: "var(--v2-text)" };

  return (
    <AdminModal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title="Add professional"
      description="Manually add a real professional to the marketplace. They will start in 'pending review' and only appear publicly after admin approves + activates."
      size="lg"
      testId="admin-professional-create-modal"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="admin-professional-create-cancel"
            className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
            style={{ borderColor: "var(--v2-border)", color: "var(--v2-text-muted)" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="admin-professional-create-submit"
            className="rounded-md px-3.5 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: "var(--v2-accent)" }}
          >
            {submitting ? "Creating…" : "Create professional"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div
            data-testid="admin-professional-create-error"
            data-error-code={errorCode || ""}
            role="alert"
            className="rounded-md p-3 text-sm"
            style={{
              background: "rgba(239,68,68,0.10)",
              border: "1px solid rgba(239,68,68,0.34)",
              color: "var(--v2-text)",
            }}
          >
            {error}
          </div>
        )}

        <div>
          <label className="text-xs uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--v2-text-muted)" }}>
            Name *
          </label>
          <input
            data-testid="admin-professional-create-name"
            className={inputCls}
            style={{ ...inputStyle, borderColor: fieldErrors.name ? "rgba(239,68,68,0.6)" : inputStyle.borderColor }}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--v2-text-muted)" }}>
            Categories *
          </label>
          <div className="flex flex-wrap gap-2">
            {activeCats.map((cat) => {
              const on = form.categories.includes(cat.slug);
              return (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => toggleCategory(cat.slug)}
                  data-testid={`admin-professional-create-cat-${cat.slug}`}
                  className="text-xs px-2.5 py-1 rounded-full border font-medium"
                  style={{
                    background: on ? "rgba(167,139,250,0.18)" : "transparent",
                    color: on ? "var(--v2-accent-strong, #A78BFA)" : "var(--v2-text-muted)",
                    borderColor: on ? "rgba(167,139,250,0.5)" : "var(--v2-border)",
                  }}
                >
                  {cat.display_name}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--v2-text-muted)" }}>
            Bio (max 500 chars) *
          </label>
          <textarea
            data-testid="admin-professional-create-bio"
            className={inputCls}
            style={{ ...inputStyle, minHeight: 90, borderColor: fieldErrors.bio ? "rgba(239,68,68,0.6)" : inputStyle.borderColor }}
            maxLength={500}
            value={form.bio}
            onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
          />
          <div className="text-[11px] text-right mt-0.5" style={{ color: "var(--v2-text-muted)" }}>
            {form.bio.length} / 500
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--v2-text-muted)" }}>
              Service area
            </label>
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, service_area_mode: "nationwide" }))}
                data-testid="admin-professional-create-area-nationwide"
                className="text-xs px-2.5 py-1 rounded-full border"
                style={{
                  background: form.service_area_mode === "nationwide" ? "rgba(167,139,250,0.18)" : "transparent",
                  color: "var(--v2-text)",
                  borderColor: "var(--v2-border)",
                }}
              >
                Nationwide
              </button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, service_area_mode: "states" }))}
                data-testid="admin-professional-create-area-states"
                className="text-xs px-2.5 py-1 rounded-full border"
                style={{
                  background: form.service_area_mode === "states" ? "rgba(167,139,250,0.18)" : "transparent",
                  color: "var(--v2-text)",
                  borderColor: "var(--v2-border)",
                }}
              >
                Specific states
              </button>
            </div>
            {form.service_area_mode === "states" && (
              <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-1">
                {US_STATES.map((s) => {
                  const on = form.service_area_states.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleState(s)}
                      className="text-[10px] px-1.5 py-0.5 rounded border font-mono"
                      style={{
                        background: on ? "rgba(167,139,250,0.18)" : "transparent",
                        color: on ? "var(--v2-accent-strong, #A78BFA)" : "var(--v2-text-muted)",
                        borderColor: on ? "rgba(167,139,250,0.5)" : "var(--v2-border)",
                      }}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--v2-text-muted)" }}>
              Years experience
            </label>
            <input
              type="number"
              min={0}
              max={70}
              data-testid="admin-professional-create-years"
              className={inputCls}
              style={inputStyle}
              value={form.years_experience}
              onChange={(e) => setForm((f) => ({ ...f, years_experience: e.target.value }))}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--v2-text-muted)" }}>
              Website URL
            </label>
            <input
              data-testid="admin-professional-create-website"
              className={inputCls}
              style={inputStyle}
              value={form.website_url}
              onChange={(e) => setForm((f) => ({ ...f, website_url: e.target.value }))}
              placeholder="https://…"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--v2-text-muted)" }}>
              Contact email *
            </label>
            <input
              type="email"
              data-testid="admin-professional-create-email"
              className={inputCls}
              style={inputStyle}
              value={form.contact_email}
              onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--v2-text-muted)" }}>
              Contact preference
            </label>
            <select
              data-testid="admin-professional-create-contact-pref"
              className={inputCls}
              style={inputStyle}
              value={form.public_contact_preference}
              onChange={(e) => setForm((f) => ({ ...f, public_contact_preference: e.target.value }))}
            >
              {CONTACT_PREFS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          {form.public_contact_preference === "phone_visible" && (
            <div>
              <label className="text-xs uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--v2-text-muted)" }}>
                Phone number
              </label>
              <input
                data-testid="admin-professional-create-phone"
                className={inputCls}
                style={inputStyle}
                value={form.phone_number}
                onChange={(e) => setForm((f) => ({ ...f, phone_number: e.target.value }))}
              />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--v2-text-muted)" }}>
              Rate range
            </label>
            <select
              data-testid="admin-professional-create-rate"
              className={inputCls}
              style={inputStyle}
              value={form.rate_range}
              onChange={(e) => setForm((f) => ({ ...f, rate_range: e.target.value }))}
            >
              {RATE_RANGES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--v2-text-muted)" }}>
            Verification proof URL
          </label>
          <input
            data-testid="admin-professional-create-proof"
            className={inputCls}
            style={inputStyle}
            value={form.verification_proof_url}
            onChange={(e) => setForm((f) => ({ ...f, verification_proof_url: e.target.value }))}
            placeholder="Link to license, certification, LinkedIn, etc."
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="admin-professional-create-is-demo"
            checked={form.is_demo}
            onChange={(e) => setForm((f) => ({ ...f, is_demo: e.target.checked }))}
          />
          <span>Flag this as a demo/sample profile (visible in listing but blocks consultations)</span>
        </label>
      </div>
    </AdminModal>
  );
}
